import type { components } from '../../api/tipos'
import type { DiaRuta, SemanaReparto } from '../../hooks/useFiltrosAgendamiento'
import type { BonoCatalogo } from './catalogo'
import type { FilaAgendamiento, ResultadoResolucion } from './filas'
import type { EntradaResolver } from './lista'

export type FilaTabla = components['schemas']['FilaTabla']
export type ClienteTabla = components['schemas']['ClienteTabla']
export type PedidoHabitual = components['schemas']['PedidoHabitual']
export type PaginaTabla = components['schemas']['Pagina_FilaTabla_']

/**
 * La tabla de clientes: lo primero que se ve al abrir el agendamiento.
 *
 * Aqui vive lo que se puede razonar sin pintar nada: como se leen los dias de
 * ruta, como se empareja el pedido habitual con el catalogo y como una fila
 * marcada se convierte en una entrada de `POST /resolver`. Separado del
 * componente porque son las reglas que mas caro cuesta equivocar -un dia
 * desplazado saca el reparto entero el dia que no es- y aqui se prueban solas.
 *
 * Dos cosas que la pantalla **no** hace, porque los datos reales lo desaconsejan:
 *
 * - **No inventa un dia.** 119 clientes no tienen ninguna ruta y 75 tienen las
 *   cinco. Se ensena lo que hay; elegir una principal seria una mentira que se
 *   descubre en la calle.
 * - **No trata la falta de historico como un error.** 31 de cada 50 clientes no
 *   tienen pedido previo en Odoo: la columna sale vacia con su motivo y la fila
 *   se puede marcar igual.
 */

export interface Dia {
  clave: DiaRuta
  etiqueta: string
  /** Para la celda de la tabla, donde caben cinco. */
  corta: string
}

export const DIAS: readonly Dia[] = [
  { clave: 'r1', etiqueta: 'Lunes', corta: 'Lun' },
  { clave: 'r2', etiqueta: 'Martes', corta: 'Mar' },
  { clave: 'r3', etiqueta: 'Miercoles', corta: 'Mie' },
  { clave: 'r4', etiqueta: 'Jueves', corta: 'Jue' },
  { clave: 'r5', etiqueta: 'Viernes', corta: 'Vie' },
]

export interface SemanaOpcion {
  clave: SemanaReparto
  etiqueta: string
}

export const SEMANAS: readonly SemanaOpcion[] = [
  { clave: 's1', etiqueta: 'Semana 1' },
  { clave: 's2', etiqueta: 'Semana 2' },
  { clave: 's3', etiqueta: 'Semana 3' },
  { clave: 's4', etiqueta: 'Semana 4' },
]

/**
 * Los dias de reparto de un cliente, en orden de semana y sin inventar ninguno.
 * `Sin ruta` es una respuesta legitima: son 119 clientes reales.
 */
export function etiquetaDias(rutas: string[] | undefined): string {
  const suyas = DIAS.filter((dia) => (rutas ?? []).includes(dia.clave))
  if (suyas.length === 0) return 'Sin ruta'
  return suyas.map((dia) => dia.corta).join(', ')
}

/**
 * El bono del catalogo que corresponde al ultimo pedido de ese cliente.
 *
 * Se empareja por garrafas y litraje, que es lo que el backend dedujo del
 * nombre de la linea. Entre dos que casan gana el que **no** tiene variante:
 * `SIN FUENTE` o `BEJIS` son decisiones que nadie ha tomado en esta pantalla, y
 * elegirlas por el administrador cambiaria el producto que se factura.
 *
 * Devuelve `null` cuando no hay con que emparejar; ninguna aproximacion, porque
 * un bono parecido es un bono distinto en la factura.
 */
export function bonoDelHabitual(
  bonos: BonoCatalogo[],
  pedido: PedidoHabitual,
): BonoCatalogo | null {
  if (!pedido.encontrado || pedido.bono == null) return null
  const candidatos = bonos.filter(
    (bono) => bono.unidades === pedido.bono && bono.litraje === pedido.litraje,
  )
  if (candidatos.length === 0) return null
  return candidatos.find((bono) => bono.variante === '') ?? candidatos[0]
}

/** Cada motivo se corrige de una forma distinta, asi que cada uno se dice. */
const MOTIVOS_HABITUAL: Record<string, string> = {
  sin_pedidos: 'Sin pedidos previos',
  sin_ficha: 'Sin ficha en Odoo',
  sin_nif: 'Sin NIF',
  ambiguo: 'Hay que elegir',
}

/**
 * Lo que ese cliente suele pedir, en una linea. Cuando no hay nada, dice **por
 * que** no lo hay: sin pedidos previos, sin ficha o sin NIF no se arreglan
 * igual, y ninguno de los tres es un error de la pantalla.
 */
export function resumenHabitual(pedido: PedidoHabitual): string {
  if (pedido.encontrado && pedido.bono != null) {
    return `${pedido.bono} x ${pedido.litraje} L`
  }
  // Un pedido que existe pero del que no se deduce el bono ensena su texto tal
  // cual: es lo que hay en Odoo, y es mas util que un hueco.
  if (pedido.encontrado && pedido.texto) return pedido.texto
  return MOTIVOS_HABITUAL[pedido.motivo] ?? 'Sin pedidos previos'
}

/** Una fila marcada a la que todavia le falta elegir el bono. */
export function sinBono(bono: BonoCatalogo | null): boolean {
  return bono === null
}

/**
 * Una fila marcada, tal y como viaja a `POST /resolver`.
 *
 * Sin bono se manda igual: el backend resuelve solo el cliente y la revision
 * dira que le falta la linea de pedido. Bloquear el marcado obligaria a elegir
 * el bono de treinta clientes antes de ver si alguno tiene la ficha mal.
 */
export function aEntradaResolver(fila: FilaTabla, bono: BonoCatalogo | null): EntradaResolver {
  return {
    nif: fila.cliente.code,
    nombre: fila.cliente.nombre,
    bono: bono ? bono.unidades : null,
    litraje: bono ? bono.litraje : null,
    variante: bono ? bono.variante : '',
  }
}

/**
 * Cose las filas marcadas con lo que respondio el backend, en el mismo orden.
 *
 * La clave es el `contact_id` y no el indice: la tabla se repagina por debajo y
 * un indice acabaria apuntando a otro cliente.
 */
export function filasDeTabla(
  marcadas: FilaTabla[],
  resultados: ResultadoResolucion[],
): FilaAgendamiento[] {
  return marcadas.map((fila, indice) => ({
    clave: fila.cliente.contact_id,
    nif: fila.cliente.code,
    resultado: resultados[indice],
  }))
}
